import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Calendar,
  CheckCircle2,
  ClipboardSignature,
  Gavel,
  History,
  Image as ImageIcon,
  MapPin,
  Pencil,
  Share2,
  Sparkles,
  Star,
  Trash2,
  Upload,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar } from '@/components/ui/avatar';
import {
  type PropertyRow,
  useMatchBuyers,
  useProperty,
  usePropertyAppointments,
  usePropertyHistory,
  usePropertyImages,
  usePropertyOffers,
  useUploadPropertyImage,
  useDeletePropertyImage,
} from '@/hooks/data/useProperties';
import { useAgents } from '@/hooks/data/useAgents';
import { fieldLabel, valueLabel } from '@/lib/historyLabels';
import { useClient, useClients } from '@/hooks/data/useClients';
import { CreateAppointmentDialog } from '@/components/appointments/CreateAppointmentDialog';
import { KundendetailsDialog } from '@/components/clients/KundendetailsDialog';
import { PropertyShareDialog } from './PropertyShareDialog';
import { EditPropertyDialog } from './EditPropertyDialog';
import { useConfirm } from '@/components/ui/confirm';
import { formatDate, formatDateTime, formatMoney, formatNumber } from '@/lib/formatLocale';
import { apiAssetUrl } from '@/lib/api';
import { useSiteListingByProperty } from '@/hooks/data/useSiteListings';
import { ListingGallery } from './SiteListingDialog';

interface Props {
  property: PropertyRow;
  isAdmin: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ViewPropertyDialog({ property: seed, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const { data: fresh } = useProperty(open ? seed.id : undefined);
  const property = fresh ?? seed;
  const [creatingNotar, setCreatingNotar] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [editing, setEditing] = useState(false);

  const fullName = [property.address, property.city].filter(Boolean).join(', ');
  // Plan v2 update 5: surface "Notar-Termin anlegen" once the offer has been
  // accepted but before the notary appointment exists.
  const canCreateNotar = property.marketing_stage === 'kaufanbot_angenommen';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center justify-between gap-2 pe-8 font-display leading-snug">
            <span className="min-w-0 break-words">
              <Building2 className="me-2 inline h-4 w-4 align-[-2px]" />
              {t('properties.details.title', 'Immobilie')} —{' '}
              <span className="font-normal text-ink-2">{fullName}</span>
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1 rounded-md border border-line bg-card px-3 py-1.5 text-xs font-semibold text-ink-2 hover:bg-paper-2"
                title={t('properties.actions.edit', 'Bearbeiten')}
              >
                <Pencil className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {t('properties.actions.edit', 'Bearbeiten')}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setSharing(true)}
                className="inline-flex items-center gap-1 rounded-md border border-line bg-card px-3 py-1.5 text-xs font-semibold text-ink-2 hover:bg-paper-2"
                title={t('property.share.title', 'Listing teilen')}
              >
                <Share2 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{t('property.share.cta', 'Teilen')}</span>
              </button>
              {canCreateNotar && (
                <button
                  type="button"
                  onClick={() => setCreatingNotar(true)}
                  className="inline-flex items-center gap-1 rounded-md bg-ink px-3 py-1.5 text-xs font-semibold text-paper hover:bg-ink/90"
                >
                  <Gavel className="h-3.5 w-3.5" />
                  {t('properties.notar.create', 'Notar-Termin anlegen')}
                </button>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="details">
          <TabsList className="w-full md:grid md:grid-cols-5">
            <TabsTrigger value="details">{t('properties.tab.details', 'Details')}</TabsTrigger>
            <TabsTrigger value="images">{t('properties.tab.images', 'Bilder')}</TabsTrigger>
            <TabsTrigger value="related">{t('properties.tab.related', 'Termine & Anbote')}</TabsTrigger>
            <TabsTrigger value="matches">{t('properties.tab.matches', 'Käufer-Matches')}</TabsTrigger>
            <TabsTrigger value="history">{t('properties.tab.history', 'Verlauf')}</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-4">
            <DetailsPanel property={property} />
          </TabsContent>
          <TabsContent value="images" className="mt-4">
            <ImagesPanel property={property} />
          </TabsContent>
          <TabsContent value="related" className="mt-4">
            <RelatedPanel property={property} />
          </TabsContent>
          <TabsContent value="matches" className="mt-4">
            <MatchesPanel property={property} />
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <HistoryPanel property={property} />
          </TabsContent>
        </Tabs>
      </DialogContent>

      {creatingNotar && (
        <CreateAppointmentDialog
          open={true}
          onOpenChange={(o) => !o && setCreatingNotar(false)}
          defaultType="notar"
          defaultPropertyId={property.id}
          defaultClientId={property.client_id}
        />
      )}

      {sharing && (
        <PropertyShareDialog
          open={true}
          onOpenChange={(o) => !o && setSharing(false)}
          propertyId={property.id}
          property={property}
          propertyLabel={[
            property.address ?? `${property.street ?? ''} ${property.street_number ?? ''}`.trim(),
            property.postal_code ? `${property.postal_code} ${property.city}` : property.city,
          ]
            .filter(Boolean)
            .join(', ')}
        />
      )}

      {editing && (
        <EditPropertyDialog
          property={property}
          open={true}
          onOpenChange={(o) => !o && setEditing(false)}
        />
      )}
    </Dialog>
  );
}

function DetailsPanel({ property: p }: { property: PropertyRow }) {
  const { t, i18n } = useTranslation();
  // Prefer the user's locale for currency formatting; fall back to de-AT.
  // Batch ZO #3: surface the seller (linked client) front-and-centre so
  // anyone opening a property sees who owns it without digging.
  const { data: seller } = useClient(p.client_id);
  const { data: listing } = useSiteListingByProperty(p.id);
  const [showSeller, setShowSeller] = useState(false);
  const sellerName = seller
    ? [seller.title, seller.first_name, seller.last_name].filter(Boolean).join(' ')
    : '—';
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Section
        title={t('properties.details.seller', 'Verkäufer')}
        className="md:col-span-2"
      >
        <Field
          label={t('properties.fields.seller', 'Eigentümer / Verkäufer')}
          value={
            seller ? (
              <button
                type="button"
                onClick={() => setShowSeller(true)}
                className="text-start underline-offset-2 hover:underline text-ink"
              >
                {sellerName}
                {/* The separator stays outside the number: `.font-mono` is an
                    LTR island in Arabic/Hebrew, and a dot inside it lands on
                    the far side of the number, away from the name. */}
                {seller.phone && (
                  <>
                    <span className="mx-1.5 text-ink-3">·</span>
                    <span className="font-mono text-ink-3">{seller.phone}</span>
                  </>
                )}
              </button>
            ) : (
              sellerName
            )
          }
        />
        {(listing?.linked_property?.assigned_agent_name || p.assigned_agent_full_name) && (
          <Field
            label={t('properties.site.responsible_agent', 'Zuständiger Makler')}
            value={listing?.linked_property?.assigned_agent_name || p.assigned_agent_full_name}
          />
        )}
        {listing && (
          <Field
            label={t('properties.site.listing_contact', 'Kontakt im Inserat')}
            value={listing.agent_name ?? '—'}
          />
        )}
      </Section>
      {seller && showSeller && (
        <KundendetailsDialog
          client={seller}
          open={showSeller}
          onOpenChange={setShowSeller}
        />
      )}

      <Section title={t('properties.details.address', 'Adresse')}>
        <Field label={t('properties.fields.address', 'Adresse')} value={p.address ?? '—'} />
        <Field label={t('properties.fields.city', 'Stadt')} value={p.city} />
        <Field
          label={`${t('properties.fields.district', 'Bezirk')} / ${t('properties.fields.postalCode', 'PLZ')}`}
          value={[p.postal_code, p.district].filter(Boolean).join(' · ') || '—'}
        />
        {(p.street || p.street_number) && (
          <Field
            label={`${t('properties.fields.street', 'Straße')} / ${t('properties.fields.streetNumber', 'Hausnummer')}`}
            value={`${p.street ?? ''} ${p.street_number ?? ''}`.trim()}
          />
        )}
        {(p.entrance || p.floor || p.door_number) && (
          <Field
            label={`${t('properties.fields.entrance', 'Stiege')} / ${t('properties.fields.floor', 'Stock')} / ${t('properties.fields.doorNumber', 'Tür')}`}
            value={[p.entrance, p.floor, p.door_number].filter(Boolean).join(' / ')}
          />
        )}
      </Section>

      <Section title={t('properties.details.specs', 'Objekt-Details')}>
        {p.object_type && (
          <Field
            label={t('properties.fields.objectType', 'Objektart')}
            value={t(`enums.object_type.${p.object_type}`, p.object_type)}
          />
        )}
        {p.building_type && (
          <Field
            label={t('properties.fields.buildingType', 'Gebäudeart')}
            value={t(`enums.building_type.${p.building_type}`, p.building_type)}
          />
        )}
        {p.rooms_count && (
          <Field label={t('properties.fields.rooms', 'Zimmer')} value={p.rooms_count} />
        )}
        {p.property_size && (
          <Field
            label={t('properties.fields.size', 'Wohnfläche (m²)')}
            value={`${p.property_size} m²`}
          />
        )}
        {p.property_area && (
          <Field
            label={t('properties.fields.area', 'Grundfläche (m²)')}
            value={`${p.property_area} m²`}
          />
        )}
        {p.purchase_price && (
          <Field
            label={t('properties.fields.price', 'Kaufpreis (EUR)')}
            value={formatMoney(Number(p.purchase_price))}
          />
        )}
      </Section>

      {(p.edi_real_number || p.kg_grundbuch || p.einlagezahl || p.b_laufnummer) && (
        <Section title={t('properties.details.grundbuch', 'Grundbuch')} className="md:col-span-2">
          {p.edi_real_number && (
            <Field
              label="EDI-Nummer"
              value={<span className="font-mono">{p.edi_real_number}</span>}
            />
          )}
          {p.kg_grundbuch && <Field label="KG / Grundbuch" value={p.kg_grundbuch} />}
          {p.einlagezahl && <Field label="Einlagezahl" value={p.einlagezahl} />}
          {p.b_laufnummer && <Field label="B-Laufnummer" value={p.b_laufnummer} />}
        </Section>
      )}
    </div>
  );
}

function ImagesPanel({ property: p }: { property: PropertyRow }) {
  const { t } = useTranslation();
  const confirmDelete = useConfirm();
  const { data: images = [] } = usePropertyImages(p.id);
  const { data: listing } = useSiteListingByProperty(p.id);
  const listingImages = listing?.images ?? [];
  const upload = useUploadPropertyImage(p.id);
  const del = useDeletePropertyImage(p.id);
  const [err, setErr] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [dragHover, setDragHover] = useState(false);
  const dragCounter = useRef(0);
  // Ein Foto, dessen Bytes fehlen, soll als leere Kachel stehen und nicht als
  // Bruchsymbol. Gemerkt wird die ADRESSE, nicht ein Ja/Nein: sonst bliebe ein
  // frisch hochgeladenes Bild bis zum nächsten Öffnen des Dialogs verborgen.
  const [broken, setBroken] = useState<Set<string>>(new Set());

  const onFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setErr(null);
    const total = files.length;
    setProgress({ done: 0, total });
    let firstFailed: string | null = null;
    for (let i = 0; i < total; i += 1) {
      try {
        await upload.mutateAsync({
          file: files[i]!,
          isPrimary: images.length === 0 && i === 0,
        });
      } catch (cause) {
        firstFailed = firstFailed ?? (cause as Error).message;
      }
      setProgress({ done: i + 1, total });
    }
    setProgress(null);
    if (firstFailed) setErr(firstFailed);
  };

  // Eine ausgewählte Datei darf nie stillschweigend verschwinden.
  //
  // Der Filter hiess `f.type.startsWith('image/')`. Auf dem iPhone kommt ein
  // HEIC-Bild aus der Dateien-App jedoch häufig mit LEEREM `type` an — der
  // Browser kennt den Typ nicht. `''.startsWith('image/')` ist falsch, die
  // Datei fiel heraus, `onFiles` lief nie, und der Makler sah: nichts. Kein
  // Upload, kein Fehler, keine Zeile. Genau so wurde es aus dem Support
  // gemeldet („ich kann keine Fotos hochladen").
  //
  // Deshalb wird jetzt zusätzlich die ENDUNG geprüft, und wenn am Ende nichts
  // übrig bleibt, sagt der Dialog warum — statt so zu tun, als sei nichts
  // passiert. Der Server nimmt HEIC übrigens an (geprüft: 201).
  const IMAGE_EXT = /\.(jpe?g|png|webp|gif|heic|heif|avif)$/i;
  const pickImages = (files: File[]): File[] =>
    files.filter((f) => f.type.startsWith('image/') || IMAGE_EXT.test(f.name));

  const takeFiles = (all: File[]) => {
    if (all.length === 0) return;
    const imgs = pickImages(all);
    if (imgs.length === 0) {
      setErr(
        t(
          'properties.images.none_recognised',
          'Keine der gewählten Dateien wurde als Bild erkannt. Bitte JPG, PNG, WEBP oder HEIC wählen.',
        ),
      );
      return;
    }
    void onFiles(imgs);
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const all = Array.from(e.target.files ?? []);
    e.target.value = '';
    takeFiles(all);
  };

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) {
      dragCounter.current += 1;
      setDragHover(true);
    }
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) setDragHover(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragHover(false);
    takeFiles(Array.from(e.dataTransfer.files));
  };

  return (
    <div
      className="space-y-4"
      onDragEnter={onDragEnter}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <label
        className={`relative flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-sm transition ${
          dragHover
            ? 'border-[hsl(var(--brand-gold))] bg-[hsl(var(--brand-gold)/0.08)]'
            : 'border-line-strong bg-paper-2 text-ink-2 hover:border-gold hover:bg-card'
        }`}
      >
        <Upload className="h-4 w-4" />
        {progress
          ? `${progress.done} / ${progress.total} ${t('properties.images.uploading_short', 'hochgeladen')}`
          : upload.isPending
            ? t('properties.images.uploading', 'Hochladen …')
            : t('properties.images.dropzone_multi', 'Bilder hier ablegen oder auswählen (JPG/PNG, mehrfach)')}
        <input
          type="file"
          // HEIC/HEIF ausdrücklich genannt: `image/*` allein reicht auf iOS nicht,
          // weil der Typ dort leer ankommen kann und die Datei dann gar nicht
          // erst im Auswahldialog auftaucht.
          accept="image/*,.heic,.heif"
          multiple
          className="hidden"
          onChange={onPick}
        />
      </label>
      {err && (
        <p className="rounded-md border border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger-bg))] px-3 py-2 text-sm text-[hsl(var(--danger))]">
          {err}
        </p>
      )}
      {images.length === 0 && listingImages.length > 0 ? null : images.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-3">
          <ImageIcon className="mx-auto mb-2 h-8 w-8 text-line-strong" />
          {t('properties.images.empty', 'Noch keine Bilder hochgeladen.')}
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {images.map((img) => (
            <div
              key={img.id}
              className="group/img relative overflow-hidden rounded-lg border border-line"
            >
              {broken.has(img.image_url) ? (
                <div className="flex aspect-[16/10] w-full items-center justify-center bg-paper-2">
                  <ImageIcon className="h-8 w-8 text-line-strong" />
                </div>
              ) : (
                <img
                  // Als PFAD geliefert („/properties/…/image/…"), nicht als
                  // fertige Adresse. Roh gerendert löst er gegen den
                  // Web-Ursprung auf, `_routes.json` leitet nur `/api/*` und
                  // `/listing/*` durch Functions, und der SPA-Rückfall
                  // antwortet mit `index.html` — Status 200, und trotzdem ein
                  // kaputtes Bild. Genau diese Auflösung hatte die Fotokarte
                  // schon; der Fotoreiter war die letzte Stelle ohne sie.
                  src={apiAssetUrl(img.image_url)}
                  alt=""
                  loading="lazy"
                  onError={() => setBroken((prev) => new Set(prev).add(img.image_url))}
                  className="aspect-[16/10] w-full object-cover"
                />
              )}
              {img.is_primary && (
                <span className="absolute start-2 top-2 fm-pill fm-pill-gold">
                  <Star className="h-3 w-3" /> {t('properties.images.primary', 'Primär')}
                </span>
              )}
              <button
                type="button"
                onClick={async () => {
                  const ok = await confirmDelete({
                    title: t('properties.images.delete_confirm', 'Bild löschen?'),
                    confirmLabel: t('common.delete', 'Löschen'),
                    destructive: true,
                  });
                  if (ok) del.mutate(img.id);
                }}
                disabled={del.isPending}
                className="absolute end-2 top-2 hidden h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition hover:bg-[hsl(var(--danger))] group-hover/img:flex"
                aria-label={t('properties.images.delete', 'Bild löschen')}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
      {listingImages.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-3">
            {t('properties.site.listing_images', 'Fotos aus dem Inserat')}
          </p>
          <ListingGallery images={listingImages} />
        </div>
      )}
    </div>
  );
}

function RelatedPanel({ property: p }: { property: PropertyRow }) {
  const { t } = useTranslation();
  const { data: appointments = [] } = usePropertyAppointments(p.id);
  const { data: offers = [] } = usePropertyOffers(p.id);

  return (
    <div className="space-y-5">
      <div className="fm-card p-5">
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg text-ink">
          <Calendar className="h-4 w-4 text-ink-3" />
          {t('properties.related.appointments', 'Termine')} ({appointments.length})
        </h3>
        {appointments.length === 0 ? (
          <p className="py-4 text-center text-sm text-ink-3">
            {t('properties.related.no_appointments', 'Keine Termine.')}
          </p>
        ) : (
          <ul className="space-y-2">
            {appointments.map((a) => (
              <li
                key={a.id}
                className="flex items-center gap-3 rounded-md border border-line bg-paper-2 px-3 py-2.5"
              >
                <Calendar className="h-4 w-4 flex-shrink-0 text-ink-3" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-ink">
                    {formatDateTime(new Date(a.appointment_date))}
                  </p>
                  <p className="text-[11px] uppercase text-ink-3 tracking-[0.08em]">
                    {t(`enums.appointment_type.${a.appointment_type}`, a.appointment_type)}
                  </p>
                </div>
                <span className="fm-pill fm-pill-neutral">
                  {t(`enums.appointment_status.${a.status}`, a.status)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="fm-card p-5">
        <h3 className="mb-3 flex items-center gap-2 font-display text-lg text-ink">
          <ClipboardSignature className="h-4 w-4 text-ink-3" />
          {t('properties.related.offers', 'Anbote')} ({offers.length})
        </h3>
        {offers.length === 0 ? (
          <p className="py-4 text-center text-sm text-ink-3">
            {t('properties.related.no_offers', 'Keine Anbote.')}
          </p>
        ) : (
          <ul className="space-y-2">
            {offers.map((o) => (
              <li
                key={o.id}
                className="flex items-center gap-3 rounded-md border border-line bg-paper-2 px-3 py-2.5"
              >
                <ClipboardSignature className="h-4 w-4 flex-shrink-0 text-ink-3" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-ink">
                    {o.offered_price
                      ? formatMoney(Number(o.offered_price))
                      : '—'}
                  </p>
                  <p className="text-[11px] text-ink-3">
                    {formatDate(new Date(o.created_at))}
                  </p>
                </div>
                <span className="fm-pill fm-pill-neutral">
                  {t(`enums.offer_status.${o.status}`, o.status)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function MatchesPanel({ property: p }: { property: PropertyRow }) {
  const { t } = useTranslation();
  const match = useMatchBuyers(p.id);
  const { data: clients = [] } = useClients({ clientType: 'kupac' });
  const clientMap = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);

  const onMatch = async (notify: boolean) => {
    await match.mutateAsync(notify);
  };

  const result = match.data;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => onMatch(false)}
          disabled={match.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-ink px-3 py-2 text-sm font-semibold text-paper hover:bg-ink/90 disabled:opacity-60"
        >
          <Sparkles className="h-4 w-4" />
          {match.isPending
            ? t('properties.match.running', 'Sucht …')
            : t('properties.match.run', 'Match jetzt prüfen')}
        </button>
        {result && result.matches.length > 0 && (
          <button
            type="button"
            onClick={() => onMatch(true)}
            disabled={match.isPending}
            className="inline-flex items-center gap-2 rounded-md border border-line bg-card px-3 py-2 text-sm font-medium text-ink hover:bg-paper-2"
          >
            {t('properties.match.notify', 'Allen passenden SMS senden')}
          </button>
        )}
      </div>

      {!result && (
        <p className="py-6 text-center text-sm text-ink-3">
          {t('properties.match.idle', 'Klick auf „Match jetzt prüfen", um passende Käufer zu finden.')}
        </p>
      )}

      {result && (
        <>
          <p className="text-sm text-ink-2">
            {result.matches.length}{' '}
            {t('properties.match.found', 'passende Käufer gefunden')}
            {result.notified > 0 && (
              <span className="ms-2 text-[hsl(var(--positive))]">
                · {result.notified} benachrichtigt
              </span>
            )}
          </p>
          {result.matches.length > 0 && (
            <ul className="space-y-2">
              {result.matches.map((m) => {
                const c = clientMap.get(m.clientId);
                const fullName = c
                  ? [c.title, c.first_name, c.last_name].filter(Boolean).join(' ')
                  : m.clientId;
                return (
                  <li
                    key={m.clientId}
                    className="flex items-center gap-3 rounded-md border border-line bg-paper-2 px-3 py-2.5"
                  >
                    <Avatar name={fullName} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink truncate">{fullName}</p>
                      <p className="text-xs text-ink-3 truncate">
                        {m.matched.join(', ') || '—'}
                      </p>
                    </div>
                    <span className="fm-pill fm-pill-gold">Score {m.score}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function HistoryPanel({ property: p }: { property: PropertyRow }) {
  const { t } = useTranslation();
  const { data: history = [] } = usePropertyHistory(p.id);
  const { data: agents = [] } = useAgents();
  const userMap = useMemo(() => new Map(agents.map((a) => [a.user_id, a])), [agents]);

  if (history.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-ink-3">
        <History className="mx-auto mb-2 h-8 w-8 text-line-strong" />
        {t('properties.history.empty', 'Keine Historie verfügbar.')}
      </p>
    );
  }

  return (
    <ul className="space-y-3">
      {history.map((h) => {
        const user = h.changed_by ? userMap.get(h.changed_by) : null;
        return (
          <li key={h.id} className="border-s-2 border-line-strong ps-3">
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-2">
                <Avatar name={user?.full_name ?? '?'} size="sm" />
                <span className="text-sm font-medium text-ink">
                  {user?.full_name ?? h.changed_by ?? '—'}
                </span>
              </span>
              <p className="text-xs text-ink-3">
                {formatDateTime(new Date(h.created_at))}
              </p>
            </div>
            <div className="ms-9 mt-1 space-y-0.5 text-xs">
              <p className="text-ink-3">{fieldLabel('property', h.field_changed)}</p>
              {(h.old_value || h.new_value) && (
                <div className="flex flex-wrap items-center gap-2">
                  {h.old_value && (
                    <span className="rounded-md border border-line px-2 py-0.5 text-ink-3 line-through">
                      {valueLabel('property', h.field_changed, h.old_value, t)}
                    </span>
                  )}
                  {h.old_value && h.new_value && <span className="text-ink-3">→</span>}
                  {h.new_value && (
                    <span className="rounded-md bg-[hsl(var(--info-bg))] px-2 py-0.5 font-medium text-[hsl(var(--info))]">
                      {valueLabel('property', h.field_changed, h.new_value, t)}
                    </span>
                  )}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// Helpers
function Section({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`fm-card p-5 ${className ?? ''}`}>
      <h3 className="mb-3 flex items-center gap-2 font-display text-lg text-ink">
        <MapPin className="h-4 w-4 text-ink-3" />
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.08em] text-ink-3">{label}</p>
      <p className="text-sm text-ink">{value}</p>
    </div>
  );
}
